import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Policy, Provisioning } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs';
import { EvaluatedAddon, PolicyService } from '../../../policy';
import { ProvisioningSessionService } from '../../services';

interface PlanInfoFormValues {
  plan: Policy.Plan.Model | null;
  addonIds: string[] | null;
  termId: string | null;
}

@Component({
  selector: 'ws-provision-plan-form',
  templateUrl: './provision-plan-form.component.html',
  styleUrls: ['./provision-plan-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ProvisionPlanFormComponent,
    },
  ],
})
export class ProvisionPlanFormComponent implements ControlValueAccessor, OnInit {
  constructor(
    private sessionSvc: ProvisioningSessionService,
    private policySvc: PolicyService
  ) {}

  @Output() nextStep = new EventEmitter<PlanInfoFormValues | null>();
  @Output() back = new EventEmitter();
  @Output() evaluation = new EventEmitter<Provisioning.EvaluatedSessionPlans | null>();

  @Output() termChanged = new EventEmitter<string>();

  @PropertyListener('session') session$ = new BehaviorSubject<Provisioning.ProvisioningSession | null>(null);
  @Input() session!: Provisioning.ProvisioningSession;

  planControl = new FormControl();

  @Input() disabled = false;
  touched = false;

  @PropertyListener('staffView') staffView$ = new BehaviorSubject<boolean | null>(null);
  @Input() staffView: boolean | null = null;

  @Output() addons = new EventEmitter<{ planId: string; termId: string; addons: EvaluatedAddon[] }[]>();

  protected planMarkups$ = this.sessionSvc.planMarkups$;
  protected hideMarkupEntry$ = this.sessionSvc.hideMarkupEntry$;

  protected onMarkupChanged(event: { termId: string; planId: string; cents: number }) {
    this.sessionSvc.setPlanMarkup(event.termId, event.planId, event.cents);
  }

  policy$ = this.session$.pipe(
    switchMap(async session => {
      if (!session || !session.policyId) return null;
      // Always fetch latest policy so checkout reflects post-publish changes
      // (e.g. term payment method edits) even for existing sessions.
      return await this.policySvc.getOne(session.policyId);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * VS-896: PLAN_FIRST renders one price card per term — so we need evaluated data for every term
   * that any plan offers, not just the term persisted on the session.
   *
   * Strategy: derive the union of terms that have at least one plan configured for them, then
   * fan out parallel `evaluateSession` calls (one per term, server honours `?termId=` override).
   * Service-layer memoization dedupes repeat calls. Results are concatenated into one flat
   * `EvaluatedSessionPlans` so the picker can treat the multi-term data as a uniform feed.
   *
   * For TERM_FIRST we fall through to the legacy single-call path — no extra network cost for
   * policies that don't opt in.
   */
  evaluatedPlans$ = combineLatest([this.session$, this.policy$, this.staffView$]).pipe(
    filter(([, , staffView]) => staffView !== null),
    map(([session, policy, staffView]) => ({ session, policy, ignoreVisibility: staffView === true })),
    distinctUntilChanged(
      (prev, curr) =>
        prev.ignoreVisibility === curr.ignoreVisibility &&
        prev.session?.id === curr.session?.id &&
        prev.session?.hash === curr.session?.hash &&
        prev.session?.policyTermId === curr.session?.policyTermId &&
        prev.policy?.id === curr.policy?.id &&
        (prev.policy?.modified ? new Date(prev.policy.modified).toISOString() : '') ===
          (curr.policy?.modified ? new Date(curr.policy.modified).toISOString() : '')
    ),
    switchMap(async ({ session, policy, ignoreVisibility }) => {
      if (!session) return null;
      const evaluationVersion = `${session.hash ?? ''}|${
        policy?.modified ? new Date(policy.modified).toISOString() : ''
      }`;

      const isPlanFirst = policy?.checkoutPlanDisplay === Policy.CheckoutPlanDisplay.PLAN_FIRST;
      if (!isPlanFirst) {
        return await this.sessionSvc.evaluateSession(session.id, evaluationVersion, ignoreVisibility);
      }

      const planFirstTermIds = this.collectPlanFirstTermIds(policy);
      // Empty term-set → no sellable plans → render with the legacy fallback so the picker still
      // surfaces the standard "no plans available" empty state instead of a stuck loader.
      if (!planFirstTermIds.length) {
        return await this.sessionSvc.evaluateSession(session.id, evaluationVersion, ignoreVisibility);
      }

      const perTermEvaluations = await Promise.all(
        planFirstTermIds.map(termId =>
          this.sessionSvc.evaluateSession(session.id, evaluationVersion, ignoreVisibility, termId)
        )
      );

      const first = perTermEvaluations[0];
      return {
        sessionId: first.sessionId,
        evaluatedPlans: perTermEvaluations.flatMap(result => result.evaluatedPlans),
      } as Provisioning.EvaluatedSessionPlans;
    }),
    tap(evaluation => this.evaluation.emit(evaluation)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Union of every term-id that at least one plan in the policy actually configures coverage for.
   * Mirrors the filter used inside `plan-picker.computePlanFirstPlans` so the fan-out only evaluates
   * terms a plan can actually sell — vehicles excluded by the pricing engine are still filtered out
   * server-side per evaluation.
   */
  private collectPlanFirstTermIds(policy: Policy.Model | null): string[] {
    if (!policy?.plans) return [];
    const termIds = new Set<string>();
    for (const plan of policy.plans) {
      for (const [termId, config] of Object.entries(plan.coverageConfigs ?? {})) {
        if (Array.isArray(config) && config.length > 0) termIds.add(termId);
      }
    }
    return Array.from(termIds);
  }

  private _onChange?: (value: PlanInfoFormValues) => void;
  private _onTouched?: () => void;

  next() {
    this.nextStep.emit(this.planControl.value ?? null);
  }

  ngOnInit(): void {
    this.planControl.valueChanges.subscribe(values => this._onChange?.(values));
  }

  writeValue(value: PlanInfoFormValues | null): void {
    this.planControl.setValue(
      {
        plan: value?.plan || null,
        addonIds: value?.addonIds || null,
        termId: value?.termId ?? null,
      },
      { emitEvent: false }
    );
  }

  registerOnChange(onChange: (value: PlanInfoFormValues) => void): void {
    this._onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this._onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this._onTouched?.();
      this.touched = true;
    }
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  goBack() {
    this.back.emit();
  }
}
